import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  signInWithCredential,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
} from 'firebase/auth';

import { firebaseAuth } from '../lib/firebase';
import { mapFirebaseAuthError } from './errors';
import {
  clearServerSession,
  createServerSession,
  requestMagicLink,
} from './session-api';

/**
 * Wraps only the Firebase call in try/catch, not the server session call that
 * follows — Firebase errors get a user-friendly message, while ApiError from
 * the session endpoint already carries a decent one.
 */
export async function signInWithGoogle() {
  let idToken: string;

  try {
    const credential = await signInWithPopup(
      firebaseAuth,
      new GoogleAuthProvider(),
    );
    idToken = await credential.user.getIdToken();
  } catch (error) {
    throw new Error(mapFirebaseAuthError(error));
  }

  return createServerSession(idToken);
}

/**
 * Completes a Google One Tap sign-in. One Tap hands back a Google ID token
 * (`credential`), not a Firebase user; trading it through
 * `GoogleAuthProvider.credential` signs into the same Firebase user the popup
 * would, so the session that follows is identical.
 */
export async function signInWithGoogleIdToken(googleIdToken: string) {
  let idToken: string;

  try {
    const credential = await signInWithCredential(
      firebaseAuth,
      GoogleAuthProvider.credential(googleIdToken),
    );
    idToken = await credential.user.getIdToken();
  } catch (error) {
    throw new Error(mapFirebaseAuthError(error));
  }

  return createServerSession(idToken);
}

export async function signOutEverywhere() {
  await clearServerSession();
  await signOut(firebaseAuth);
}

/**
 * Asks the API to email `email` a Firebase sign-in link, and says whether
 * that address already has an account (`existingAccount`). The link's continue
 * URL already carries the email as a query param (see apps/api's
 * MagicLinkService), so the verify page never needs to ask for it again —
 * not even on a different device/browser than the one that requested it.
 */
export async function sendMagicLink(email: string, redirect?: string) {
  return requestMagicLink(email, redirect);
}

/**
 * Completes sign-in from a clicked magic-link URL. Firebase encodes the
 * whole verification state (mode, oobCode, ...) into the URL itself, so
 * `window.location.href` is the one required input here.
 */
export async function completeMagicLinkSignIn(email: string) {
  const link = window.location.href;

  if (!isSignInWithEmailLink(firebaseAuth, link)) {
    throw new Error('This sign-in link is invalid or has expired.');
  }

  let idToken: string;

  try {
    const credential = await signInWithEmailLink(firebaseAuth, email, link);
    idToken = await credential.user.getIdToken();
  } catch (error) {
    throw new Error(mapFirebaseAuthError(error));
  }

  return createServerSession(idToken);
}
